import React, { useMemo, useRef } from 'react'
import { makeStyles } from 'tss-react/mui'
import { ExtendableStyles } from 'utils-next/types'

import { Theme, Typography } from '@mui/material'

import { JoditEditor } from './JoditEditor.component'

export type HtmlEditorProps = ExtendableStyles & {
  label?: string
  value?: string
  initialContent?: string
  helperText?: string
  maxLength?: number
  onChange: (html: string) => void
}

const defaultConfig: any = {
  enter: 'BR',
  defaultActionOnPaste: 'insert_clear_html',
  toolbarAdaptive: false,
  readonly: false,
  buttons: [
    'source',
    '|',
    'bold',
    'strikethrough',
    'underline',
    'italic',
    'eraser',
    '|',
    'superscript',
    'subscript',
    '|',
    'ul',
    'ol',
    '|',
    'outdent',
    'indent',
    '|',
    'brush',
    'link',
    '|',
    'undo',
    'redo',
    'selectall',
    'cut',
    'copy',
    'paste',
    '|',
    'fullsize',
    'print',
    'about',
  ],
}

function HtmlEditor({
  className,
  style,
  value = '',
  initialContent = '',
  label,
  maxLength,
  helperText,
  onChange,
}: HtmlEditorProps) {
  const ref = useRef<any>(null)
  const { classes } = useStyles()
  const config = useMemo(
    () => ({
      ...defaultConfig,
      limitChars: maxLength,
    }),
    [maxLength]
  )
  return (
    <div className={className} style={style}>
      {label && <Typography variant="body2">{label}</Typography>}
      {helperText && (
        <Typography className={classes.helperText} variant="body2">
          {helperText}
        </Typography>
      )}
      <JoditEditor
        ref={ref}
        key={initialContent}
        config={config}
        value={value}
        onBlur={(newContent) => onChange(newContent)} // preferred to use only this option to update the content for performance reasons
        onChange={undefined}
      />
    </div>
  )
}

const useStyles = makeStyles()(({ spacing, palette, typography }: Theme) => ({
  helperText: {
    marginBottom: spacing(),
    fontSize: typography.caption.fontSize,
    color: palette.action.active,
  },
}))

export default React.memo(HtmlEditor)
